import React, { useState, useEffect } from 'react';

import { ActionIcon, Button, Group, LoadingOverlay } from '@mantine/core';
import { useTranslations } from 'shared/translations/useTranslations';
import useFetch from 'shared/hooks/useFetch';
import apiService from 'shared/services/api';
import { Pencil, Trash } from 'tabler-icons-react';
import AppContent from 'shared/components/AppContent';
import ListHeading from 'shared/components/ListHeading';
import { Table, Tbody, Td, Th, Thead, Tr } from '../../../../shared/components/Table';
import EditLimitModal from './EditLimitModal';
import { useIsSuperAdmin } from '../../../../shared/hooks/useIsSuperAdmin';
import { useSelector } from 'react-redux';
import { USER, USER_CONTROLLER } from '../../../../shared/constants/user';
import CreateLimitModal from './CreateLimitModal';
import { PlaceLimitRule, typeToLabel } from './types';
import EditLimitV2Modal from './EditLimitV2Modal';
import ConfirmModal from '../../../../shared/components/ConfirmModal';
import { toast } from 'react-toastify';

interface Props {
  placeId: string | number;
}

function Limits({ placeId }: Props) {
  const translations = useTranslations();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [limitToEdit, setLimitToEdit] = useState<PlaceLimitRule | undefined>();
  const [limitToDelete, setLimitToDelete] = useState<PlaceLimitRule | undefined>();
  const {
    data: limitsData,
    isLoading: isLoadingLimits,
    isLoaded: isLoadedLimits,
    error: errorLimits,
    refresh: refreshLimits,
  } = useFetch<{
    limits: PlaceLimitRule[];
  }>({
    initialValue: { limits: [] },
    fetchAction: () => apiService({}).get(`/api/places/${placeId}/limits`),
  });
  const { data, isLoading, isLoaded, error, refresh } = useFetch<{
    deviceTypes: any[];
  }>({
    initialValue: { deviceTypes: [] },
    fetchAction: () => apiService({}).get(`/api/places/${placeId}/device-types?withObjects=true`),
  });
  const {
    data: devicesData,
    isLoading: isLoadingDevices,
    isLoaded: isLoadedDevices,
    error: errorDevices,
    refresh: refreshDevices,
  } = useFetch<{
    devices: any[];
  }>({
    initialValue: { devices: [] },
    fetchAction: () => apiService({}).get(`/places/${placeId}/devices`),
  });
  const {
    data: zonesData,
    isLoading: isLoadingZones,
    isLoaded: isLoadedZones,
    error: errorZones,
    refresh: refreshZones,
  } = useFetch<{
    zones: any[];
  }>({
    initialValue: { zones: [] },
    fetchAction: () => apiService({}).get(`/api/places/${placeId}/zones`),
  });
  const [selectedEditId, setSelectedEditId] = useState<string | undefined>();
  const handleRefresh = () => {
    refresh();
    refreshLimits();
  };
  const handleModalClose = () => {
    setSelectedEditId(undefined);
  };
  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems(data.deviceTypes);
  }, [data.deviceTypes]);
  const isSuperAdmin = useIsSuperAdmin();
  const userType = useSelector(state => state.auth.data.userType);
  const readOnly = [USER_CONTROLLER, USER].includes(userType);
  const [isDeleting, setIsDeleting] = useState(false);
  const handleDeleteLimit = async () => {
    try {
      setIsDeleting(true);

      await apiService({}).delete(`/api/places/${placeId}/limits/${limitToDelete?.id}`);
      setIsDeleting(false);
      setLimitToDelete(undefined);
      handleRefresh();
    } catch {
      setIsDeleting(false);
      toast.error(translations.global.somethingWentWrong);
    }
  };
  return (
    <div style={{ position: 'relative' }}>
      <CreateLimitModal
        open={createModalOpen}
        placeId={placeId}
        deviceTypes={data.deviceTypes || []}
        onClose={() => setCreateModalOpen(false)}
        refreshParent={handleRefresh}
        devices={devicesData.devices || []}
        zones={zonesData.zones || []}
        placeUuid={placeId as string}
        currentLimits={limitsData.limits || []}
      />
      <EditLimitV2Modal
        open={!!limitToEdit}
        currentLimits={limitsData.limits || []}
        placeId={placeId}
        deviceTypes={data.deviceTypes || []}
        onClose={() => setLimitToEdit(undefined)}
        refreshParent={handleRefresh}
        devices={devicesData.devices || []}
        zones={zonesData.zones || []}
        placeUuid={placeId as string}
        limitToEdit={limitToEdit}
      />
      {/* <DeleteLimitV2Modal
        open={!!limitToDelete}
        placeId={placeId}
        onClose={() => setLimitToDelete(undefined)}
        refreshParent={handleRefresh}
        limitToDelete={limitToDelete}
      /> */}

      <ConfirmModal
        isOpen={!!limitToDelete}
        paragraph={<div>Czy na pewno chcesz usunąć ograniczenie?</div>}
        isLoading={isDeleting}
        onClose={() => setLimitToDelete(undefined)}
        onConfirm={handleDeleteLimit}
        title={`Czy na pewno chcesz usunąć ograniczenie?`}
        confirmLabel={`Usuń`}
        cancelLabel={translations.global.cancel}
      />

      <EditLimitModal
        placeId={placeId}
        deviceTypeId={selectedEditId}
        onClose={handleModalClose}
        refreshParent={handleRefresh}
      />
      <ListHeading title={translations.limits.title}>
        <Button onClick={() => setCreateModalOpen(true)}>{translations.global.add}</Button>
      </ListHeading>
      <AppContent>
        <LoadingOverlay visible={isLoading || isLoadingLimits} />
        <Table>
          <Thead>
            <Tr>
              <Th>#</Th>
              <Th>Nazwa</Th>
              <Th>Typ</Th>
              <Th>Strefa</Th>
              <Th>Typ urządzenia</Th>
              <Th>Urządzenia</Th>
              <Th>Limit szkodników normalnych</Th>
              <Th>Limit szkodników niebezpiecznych</Th>
              <Th></Th>
            </Tr>
          </Thead>

          <Tbody>
            {limitsData.limits?.map((limit, index) => (
              <Tr key={limit.id}>
                <Td>{index + 1}.</Td>
                <Td>{limit.name}</Td>
                <Td>{typeToLabel[limit.type]}</Td>
                <Td>{limit.zone?.map(zone => zone.name).join(', ')}</Td>
                <Td>{limit.deviceType?.map(deviceType => deviceType.name).join(', ')}</Td>
                <Td>{limit.devices?.map(device => device.uniqueId).join(', ')}</Td>
                <Td>{limit.normalSum}</Td>
                <Td>{limit.dangerousSum}</Td>
                <Td>
                  <Group>
                    <Button
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setLimitToEdit(limit);
                      }}
                      compact
                      size="xs"
                      color="blue">
                      <Pencil size={14} />
                    </Button>
                    <Button
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setLimitToDelete(limit);
                      }}
                      compact
                      size="xs"
                      color="red">
                      <Trash size={14} />
                    </Button>
                  </Group>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        <Table isLoading={isLoading}>
          <Thead>
            <Tr>
              <Th>{translations.global.name}</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>xx</Td>
            </Tr>
            {data.deviceTypes?.map(limit => {
              return (
                <Tr key={limit.id}>
                  <Td>{limit.name}</Td>
                  <Td>
                    <Group ml="auto">
                      {/* {!isSuperAdmin && ( */}
                      <ActionIcon
                        variant="transparent"
                        onClick={() => {
                          setSelectedEditId(limit.id);
                        }}>
                        <Pencil size={16} />
                      </ActionIcon>
                      {/* )} */}
                    </Group>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </AppContent>
    </div>
  );
}

export default Limits;
